import React, { useContext } from 'react'

import * as Sentry from '@sentry/browser'
import { Features } from 'generated/featureDefinitions'
import { AllFeatureIds } from 'generated/featureIds.generated'
import {
  AttendanceRootFeaturesDocument,
  AttendanceRootFeaturesQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import useFetchData from 'modules/billing/hooks/useFetchData'

interface ContextType {
  attendanceFeatures: Map<string, boolean>
  loading: boolean
  timeTrackingNotInPlan: boolean
}
type AttendanceFeature =
  AttendanceRootFeaturesQuery['companies']['featureAvailabilitiesConnection']['nodes'][number]
type Feature = AttendanceRootFeaturesQuery['features']['featuresConnection']['nodes'][number]

const featureAvailabilityIds: string[] = [
  Features.projects_management,
  Features.projects_flexible_tracking,
  Features.projects_overview,
  Features.projects_with_subprojects,
  Features.time_tracking_time_alerts,
  Features.time_tracking_geofencing_alerts,
  Features.timeoff_overtime_compensation,
  Features.time_tracking_real_time_attendance,
  Features.time_tracking_edit_timesheet_authorization,
  Features.time_tracking_autofill_timesheet,
  Features.time_tracking_automatic_breaks,
  Features.time_tracking_automatic_planned_breaks,
  Features.time_tracking_face_recognition,
  Features.time_tracking_ranges_and_tolerances,
  Features.time_tracking_overtime_requests,
  Features.time_balance_annualisation_export,
  Features.time_tracking_multi_policies,
  Features.time_tracking_special_hours_brazil,
  Features.payroll_management, // used in payroll compensation
  Features.compensations_management,
  Features.time_tracking_corrective_breaks,
  Features.engagement_employee_pulse_questions,
]
const featureIds: AllFeatureIds[] = [
  'dev_time_tracking_complementary_hours',
  'dev_hour_balance_ui',
  'hour_balance_visibility',
]
const Context = React.createContext<ContextType | undefined | null>(null)

export const AttendanceFeaturesProvider = ({ children }: { children: React.ReactNode }) => {
  const { company } = useCurrentSession()
  const { loading, data } = useQuery(AttendanceRootFeaturesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      availabilityIds: featureAvailabilityIds,
      featureIds: featureIds,
      companyId: company?.id,
    },
  })
  const [attendanceFeatures, _setAttendanceFeatures] = React.useState<Map<string, boolean>>(
    new Map<string, boolean>()
  )
  const featuresData = data?.companies?.featureAvailabilitiesConnection.nodes ?? []
  const featuresConnectionData = data?.features?.featuresConnection?.nodes ?? []
  const { loading: loadingTimeTracking, isNotFound: timeTrackingNotInPlan } =
    useFetchData('time_tracking')

  useEffect(() => {
    if (!loading) {
      featuresData.forEach((feature: AttendanceFeature) => {
        attendanceFeatures.set(feature.id, feature.available)
      })

      featuresConnectionData.forEach((feature: Feature) => {
        attendanceFeatures.set(feature.name, feature.enabled)
      })

      _setAttendanceFeatures(attendanceFeatures)
    }
  }, [loading])

  return (
    <Context.Provider
      value={{
        attendanceFeatures,
        loading: loading || loadingTimeTracking,
        timeTrackingNotInPlan,
      }}
    >
      {children}
    </Context.Provider>
  )
}

const useAttendanceFeaturesContext = (): ContextType => {
  const AttendanceFeaturesContext = useContext(Context)
  if (!AttendanceFeaturesContext) {
    throw new Error('useAttendanceFeatures must be used within an AttendanceFeaturesProvider')
  }
  return AttendanceFeaturesContext
}

export const useAttendanceFeatures = (featureId: string) => {
  const { attendanceFeatures, loading } = useAttendanceFeaturesContext()
  const featureAvailability: boolean | undefined = attendanceFeatures.get(featureId)

  if (!loading && !!attendanceFeatures.size && featureAvailability === undefined) {
    const error = `Feature Id "${featureId}" is missing, please add it in the featureIds array inside AttendanceFeaturesProvider`
    Sentry.captureException(error)
  }

  return {
    loading,
    featureAvailability: !!featureAvailability,
  }
}

export const useTimeTrackingSubscriptionAddon = () => {
  const { timeTrackingNotInPlan } = useAttendanceFeaturesContext()

  return {
    timeTrackingNotInPlan,
  }
}
